/** @jsxImportSource @emotion/react */

import { useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from 'components/Button';
import { Checkbox, Submit } from 'components/Form';
import { Icon } from 'components/Icon';
import { Text } from 'components/Text';
import { TrackingContext } from 'context/Tracking';
import { useStyles } from 'utils/useStyles';

import { recoveryCodesStyles } from './RecoveryCodes.styles';

interface RecoveryCodesFormValues {
  confirmed: boolean;
}

interface RecoveryCodesProps {
  recoveryCodes: string[];
  onComplete: () => void;
}

export const RecoveryCodes = ({ recoveryCodes, onComplete }: RecoveryCodesProps) => {
  const styles = useStyles(recoveryCodesStyles);
  const [, { faro }] = useContext(TrackingContext);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RecoveryCodesFormValues>({
    mode: 'onSubmit',
    defaultValues: { confirmed: false },
  });

  const handleDownloadRecoveryCodes = useCallback(() => {
    if (recoveryCodes.length === 0) {
        return;
    }

    try {
      const content = recoveryCodes.join('\n');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'grafana-mfa-recovery-codes.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download recovery codes:', err);
      faro.trackError(err as Error);
    }
  }, [recoveryCodes, faro]);

  const handlePrintRecoveryCodes = useCallback(() => {
    if (recoveryCodes.length === 0) {
        return;
    }

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.warn('Print window was blocked. Please allow popups and try again.');
        return;
      }

      printWindow.document.write(generatePrintHtml(recoveryCodes));
      printWindow.document.close();
      printWindow.print();
    } catch (err) {
      console.error('Failed to print recovery codes:', err);
      faro.trackError(err as Error);
    }
  }, [recoveryCodes, faro]);

  return (
    <div css={styles.stepContent}>
      <div css={styles.successMessage}>
        <Icon name="check" title="Success" />
        <Text type="normal">Two-factor authentication enabled</Text>
      </div>

      <Text align="center" type="large" css={styles.title}>
        Save your recovery codes
      </Text>

      <div css={styles.warningBox} role="alert">
        <Text type="normal" align="left">
          <strong>Important:</strong> Store these codes securely. You&apos;ll need them if you lose access to your
          authenticator app.
        </Text>
      </div>

      <div css={styles.recoveryCodesContainer}>
        <div css={styles.recoveryCodesGrid}>
          {recoveryCodes.map((code, index) => (
            <Text
              key={code}
              css={styles.recoveryCode}
              type="normal"
              aria-label={`Recovery code ${index + 1}`}
            >
              {code}
            </Text>
          ))}
        </div>

        <div css={styles.actionButtons}>
          <Button
            label="Download"
            onClick={handleDownloadRecoveryCodes}
            ariaLabel="Download recovery codes as text file"
            css={styles.customButton}
            trackingLabel="mfa-setup-download-recovery-codes"
          />
          <Button
            label="Print"
            onClick={handlePrintRecoveryCodes}
            ariaLabel="Print recovery codes"
            css={styles.customButton}
            trackingLabel="mfa-setup-print-recovery-codes"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onComplete)} noValidate>
        <div css={styles.checkboxContainer} className={errors.confirmed ? 'error' : ''}>
          <Checkbox
            control={control}
            name="confirmed"
            label="I have saved my recovery codes"
            validate={[validateConfirmed]}
          />
          <Text css={styles.checkboxError} type="small" className={!errors.confirmed ? 'hidden' : ''}>
            {errors.confirmed?.message || '\u00A0'}
          </Text>
        </div>

        <Submit
          label="Continue to account"
          ariaLabel="Complete MFA setup and continue to account"
          css={styles.submitButton}
        />
      </form>
    </div>
  );
};

const validateConfirmed = (value: boolean) => {
  if (!value) {
    return 'Please confirm that you have saved your recovery codes';
  }
  return true;
};

const escapeHtml = (str: string) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

const generatePrintHtml = (recoveryCodes: string[]) => `
  <html>
    <head>
      <title>Grafana MFA Recovery Codes</title>
      <style>
        body { font-family: monospace; padding: 20px; }
        h1 { margin-bottom: 20px; }
        code { display: block; margin: 10px 0; padding: 10px; background: #f5f5f5; }
      </style>
    </head>
    <body>
      <h1>Grafana MFA Recovery Codes</h1>
      <p><strong>Store these codes securely. You'll need them if you lose access to your authenticator app.</strong></p>
      ${recoveryCodes.map((code) => `<code>${escapeHtml(code)}</code>`).join('')}
    </body>
  </html>
`;
